import { isEmpty } from 'lodash';
import { Backdrop, Box, Link, MarkedText, Tooltip, Typography } from '@common-components';
import { useBoolean } from 'hooks';

import { messages } from 'i18n';
import { ExtendedMarketRequest } from 'types';
import { ConditionalWrap, getDisplayDate } from 'utils';
import { DirtyActionsGuard } from 'broker/components/DirtyContentGuard/DirtyActionsGuard';
import InformationForm from './InformationForm';

interface IndicationProps {
  marketRequest: ExtendedMarketRequest;
}

export default function InformationRequest({ marketRequest }: IndicationProps) {
  const [isOpen, { off: close, on: open }] = useBoolean(false);

  const otherInformationRequest = marketRequest.informationRequested.find((item) => item.type === 'Other');

  const isEmptyState = otherInformationRequest?.details === messages.marketRequest.informationRequestEmptyState;

  const { date: informationRequestDate, isAIExtracted, inboundEmailMessageId } = otherInformationRequest || {};

  const dateText = informationRequestDate ? ` - ${getDisplayDate(informationRequestDate)}` : '';

  const informationContent = () => {
    if (!isEmpty(marketRequest.informationRequested)) {
      const request = marketRequest.informationRequested;

      return request?.map((item) => item.details).join(', ');
    }
    return messages.marketRequest.informationRequestEmptyState;
  };

  return (
    <DirtyActionsGuard>
      {({ closeAttemptWithAction, closeWithoutPromptWithAction, isContentDirty, setIsContentDirty }) => (
        <Tooltip
          open={isOpen}
          tooltipContent={
            <InformationForm
              isContentDirty={isContentDirty}
              setIsContentDirty={setIsContentDirty}
              closeWithoutPrompt={() => closeWithoutPromptWithAction(close)}
              id={marketRequest.id}
              informationText={isEmptyState ? '' : informationContent()}
              marketName={marketRequest.marketName}
              inboundEmailMessageId={inboundEmailMessageId}
            />
          }
        >
          <Box>
            <Typography
              component="div"
              onClick={open}
              sx={{
                '&: hover': {
                  textDecoration: 'underline',
                },
              }}
              variant="body2"
              color="text.primary"
            >
              <ConditionalWrap
                condition={isEmptyState}
                wrap={(wrappedChildren) => <Link sx={{ cursor: 'pointer' }}>{wrappedChildren}</Link>}
              >
                {isEmptyState ? (
                  messages.marketRequest.informationRequestEmptyState
                ) : (
                  <Typography variant="body2" sx={{ cursor: 'pointer' }}>
                    <ConditionalWrap
                      condition={!!isAIExtracted}
                      wrap={(wrappedChildren) => <MarkedText>{wrappedChildren}</MarkedText>}
                    >
                      {`${messages.marketRequest.informationRequested} ${dateText}`}
                    </ConditionalWrap>
                  </Typography>
                )}
              </ConditionalWrap>
            </Typography>
            <Backdrop
              invisible
              open={isOpen}
              sx={{ zIndex: (theme) => theme.zIndex.tooltip - 1 }}
              onClick={() => closeAttemptWithAction(close)}
            />
          </Box>
        </Tooltip>
      )}
    </DirtyActionsGuard>
  );
}
